import { Button, Input, message, Page, Text } from 'components';
import React from 'react';
import styled from 'styled-components';
import findDuplicateUsersForNavica from 'api/UserMlsRosterController/findDuplicateUsersForNavica';
import processDuplicateUsersReportApi, {
  type ProcessDuplicateUsersReportResponse,
} from 'api/UserMlsRosterController/processDuplicateUsersReport';

const CSV_HEADERS = [
  'Key',
  'Roster',
  'MatchedUserId',
  'Name',
  'Status',
  'Key Status',
  'Lockboxes Assigned',
];

const DuplicateUsersForNavicaPage = () => {
  const initialMlsType =
    new URLSearchParams(window.location.search).get('mlsType') || '';
  const [isLoading, setIsLoading] = React.useState(false);
  const [mlsType, setMlsType] = React.useState(initialMlsType);
  const [processedCsv, setProcessedCsv] =
    React.useState<ProcessDuplicateUsersReportResponse | null>(null);
  const [uploadedFile, setUploadedFile] = React.useState<File | null>(null);
  const [isProcessing, setIsProcessing] = React.useState(false);
  const uploadSessionRef = React.useRef(0);

  const onCsvUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const file = e.target.files?.[0];
      if (!file) return;

      uploadSessionRef.current += 1;
      setProcessedCsv(null);
      setIsProcessing(false);
      setUploadedFile(file);
      message.success(
        'File uploaded. Click "Process Duplicate Users" to parse it.',
      );
    } catch (err) {
      message.error((err as Error)?.message || String(err));
    } finally {
      e.target.value = '';
    }
  };

  const processDuplicateUsersReport = async () => {
    if (!uploadedFile) {
      message.error('No file uploaded');
      return;
    }

    const uploadSession = uploadSessionRef.current;

    try {
      setIsProcessing(true);
      const content = await uploadedFile.text();
      const result = await processDuplicateUsersReportApi({
        csvContent: content,
      });

      if (uploadSession !== uploadSessionRef.current) {
        return;
      }

      setProcessedCsv(result);
      message.success('CSV processed successfully.');
    } catch (err) {
      if (uploadSession === uploadSessionRef.current) {
        setProcessedCsv(null);
      }
      message.error((err as Error)?.message || String(err));
    } finally {
      if (uploadSession === uploadSessionRef.current) {
        setIsProcessing(false);
      }
    }
  };

  const generateReport = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const trimmedMlsType = mlsType.trim();
    if (!trimmedMlsType) {
      message.error('mlsType is required');
      return;
    }

    try {
      setIsLoading(true);
      const { blob, filename } = await findDuplicateUsersForNavica({
        mlsType: trimmedMlsType,
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      message.success('Duplicate users CSV downloaded.');
    } catch (err) {
      message.error((err as Error)?.message || String(err));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Page type="filled" title="Duplicate Users Report">
      <Layout>
        <Text>
          This tool is mainly used to detect duplicate users that were created
          during office transfers for MLSes that use the Navica MLS provider.
          Their member ID in the roster is prefixed with the office ID, so their
          UID will change with each office transfer, as well as the MLS unique
          ID, causing a duplicate user to be created.
        </Text>
        <Text>
          Enter an MLS Type to generate and download the duplicate users CSV.
        </Text>
        <form onSubmit={generateReport}>
          <StyledInput
            addonAfter={
              <Button
                disabled={isLoading || !mlsType.trim()}
                loading={isLoading}
                htmlType="submit"
              >
                Generate report
              </Button>
            }
            value={mlsType}
            onChange={e => setMlsType(e.target.value)}
            placeholder="MLS Type (e.g. PBBOR)"
          />
        </form>

        <UploadSection>
          <Text>
            Upload a duplicate users CSV from this page to parse and review it.
          </Text>
          <input type="file" accept=".csv,text/csv" onChange={onCsvUpload} />
          {uploadedFile && (
            <>
              <InstructionText>
                Click the button to process duplicate users. For each
                inactive/active pair in the CSV, the key will be transferred to
                the active user record if applicable, and inactive users will be
                soft deleted. If any lockboxes need to be transferred, they must
                be manually transferred by the BrokerBay admin before processing
                the file.
              </InstructionText>
              <ButtonContainer>
                <Button
                  disabled={isProcessing}
                  loading={isProcessing}
                  onClick={processDuplicateUsersReport}
                >
                  Process Duplicate Users
                </Button>
                <FileNameText>{uploadedFile.name}</FileNameText>
              </ButtonContainer>
            </>
          )}

          {processedCsv ? (
            <SummaryPanel>
              <SummaryTitle>Processing Summary</SummaryTitle>
              <StatsRow>
                <StatItem>
                  <StatLabel>Total Name Groups</StatLabel>
                  <StatValue>{processedCsv.summary.totalNameGroups}</StatValue>
                </StatItem>
                <StatItem>
                  <StatLabel>Active/Inactive Pairs</StatLabel>
                  <StatValue>{processedCsv.summary.totalPairs}</StatValue>
                </StatItem>
                <StatItem>
                  <StatLabel>Pairs With Key</StatLabel>
                  <StatValue>
                    {processedCsv.summary.pairsWithSupraSubscription}
                  </StatValue>
                </StatItem>
                <StatItem>
                  <StatLabel>Keys Transferred</StatLabel>
                  <StatValue>
                    {processedCsv.summary.keysTransferredCount}
                  </StatValue>
                </StatItem>
                <StatItem>
                  <StatLabel>Inactive Users Deleted</StatLabel>
                  <StatValue>
                    {processedCsv.summary.inactiveUsersDeletedCount}
                  </StatValue>
                </StatItem>
              </StatsRow>

              <LogsRow>
                <LogPanel>
                  <LogTitle>
                    Key Transfers ({processedCsv.summary.keysTransferredCount})
                  </LogTitle>
                  {processedCsv.summary.keyTransfers.length ? (
                    <LogList>
                      {processedCsv.summary.keyTransfers.map(
                        ({ name, fromUserId, toUserId }) => (
                          <LogListItem
                            key={`${name}-${fromUserId}-${toUserId}`}
                          >
                            <strong>{name}</strong>: {fromUserId}
                            {' -> '}
                            {toUserId}
                          </LogListItem>
                        ),
                      )}
                    </LogList>
                  ) : (
                    <Text>No key transfers were performed.</Text>
                  )}
                </LogPanel>

                <LogPanel>
                  <LogTitle>
                    Deleted Inactive User IDs (
                    {processedCsv.summary.inactiveUsersDeletedCount})
                  </LogTitle>
                  {processedCsv.summary.deletedUserIds.length ? (
                    <LogList>
                      {processedCsv.summary.deletedUserIds.map(userId => (
                        <LogListItem key={userId}>{userId}</LogListItem>
                      ))}
                    </LogList>
                  ) : (
                    <Text>No inactive users were deleted.</Text>
                  )}
                </LogPanel>
              </LogsRow>

              {processedCsv.groups.length > 0 ? (
                <TableWrap>
                  <table>
                    <thead>
                      <tr>
                        {CSV_HEADERS.map(header => (
                          <th key={header}>{header}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {processedCsv.groups
                        .flatMap(group => group.rows)
                        .slice(0, 20)
                        .map(row => (
                          <tr
                            key={`${row.key}-${row.rosterId}-${row.matchedUserId}-${row.status}`}
                          >
                            <td>{row.key}</td>
                            <td>{row.rosterId}</td>
                            <td>{row.matchedUserId}</td>
                            <td>{row.name}</td>
                            <td>{row.status}</td>
                            <td>{row.keyStatus || ''}</td>
                            <td>{row.lockboxesAssigned || ''}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </TableWrap>
              ) : null}
            </SummaryPanel>
          ) : null}
        </UploadSection>
      </Layout>
    </Page>
  );
};

export default DuplicateUsersForNavicaPage;

const Layout = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 10px;
  padding: 20px;
  background: white;
`;

const StyledInput = styled(Input)`
  width: 500px;
`;

const UploadSection = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 10px;
  margin-top: 12px;
`;

const SummaryPanel = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 10px;
  padding: 12px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  background: #fafafa;
`;

const SummaryTitle = styled.h3`
  margin: 0;
  font-size: 16px;
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 10px;
`;

const StatItem = styled.div`
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  background: #fff;
  padding: 8px;
`;

const StatLabel = styled.div`
  font-size: 12px;
  color: #666;
`;

const StatValue = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #333;
`;

const LogsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 10px;
`;

const LogPanel = styled.div`
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  background: #fff;
  padding: 10px;
`;

const LogTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 8px;
`;

const LogList = styled.ul`
  margin: 0;
  padding-left: 16px;
  max-height: 220px;
  overflow: auto;
`;

const LogListItem = styled.li`
  margin-bottom: 4px;
  font-size: 12px;
  word-break: break-word;
`;

const TableWrap = styled.div`
  margin-top: 10px;
  overflow: auto;

  table {
    width: 100%;
    border-collapse: collapse;
    min-width: 680px;
  }

  th,
  td {
    border: 1px solid #e5e5e5;
    padding: 6px 8px;
    text-align: left;
    font-size: 12px;
  }

  th {
    background: #f4f4f4;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  gap: 12px;
  align-items: center;
  margin-top: 8px;
`;

const InstructionText = styled(Text)`
  margin-top: 8px;
  max-width: 920px;
  padding: 8px 10px;
  border: 1px solid #ffe58f;
  border-radius: 6px;
  background: #fffbe6;
  color: #8a6d3b;
  font-size: 13px;
  line-height: 1.45;
`;

const FileNameText = styled(Text)`
  font-size: 14px;
  color: #666;
`;
